import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  Settings, 
  Save, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle,
  Info,
  Database,
  Zap,
  Shield,
  Eye,
  EyeOff,
  Trash2,
  Download,
  Upload,
  Wrench
} from 'lucide-react';
import { doc, setDoc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

// Função debounce para evitar muitas chamadas ao Firebase
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

interface AdvancedSettings {
  debugMode: boolean;
  logLevel: 'debug' | 'info' | 'warn' | 'error';
  cacheEnabled: boolean;
  cacheSize: number;
  autoSave: boolean;
  notificationsEnabled: boolean;
  performanceMonitoring: boolean;
  errorReporting: boolean;
  analyticsEnabled: boolean;
}

const defaultSettings: AdvancedSettings = {
  debugMode: false,
  logLevel: 'info',
  cacheEnabled: true,
  cacheSize: 50,
  autoSave: true,
  notificationsEnabled: true,
  performanceMonitoring: true,
  errorReporting: true,
  analyticsEnabled: false,
};

export const AdvancedSettings: React.FC = () => {
  const [settings, setSettings] = useState<AdvancedSettings>(defaultSettings);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showCacheDetails, setShowCacheDetails] = useState(false);
  const [cacheStats, setCacheStats] = useState({
    contracts: 0,
    employees: 0,
    templates: 0,
    notifications: 0,
    totalSize: '0 MB'
  });

  // Carregar configurações salvas
  useEffect(() => {
    const savedSettings = localStorage.getItem('advanced_settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings({ ...defaultSettings, ...parsed });
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
      }
    }
    
    // Configurações de manutenção movidas para página separada
    
    // Calcular estatísticas reais do cache
    calculateCacheStats();
  }, []);

  const calculateCacheStats = () => {
    try {
      // Contar itens no localStorage
      const contracts = localStorage.getItem('contracts') ? JSON.parse(localStorage.getItem('contracts')!).length : 0;
      const employees = localStorage.getItem('employees') ? JSON.parse(localStorage.getItem('employees')!).length : 0;
      const templates = localStorage.getItem('message_templates') ? JSON.parse(localStorage.getItem('message_templates')!).length : 0;
      const notifications = localStorage.getItem('system_logs') ? JSON.parse(localStorage.getItem('system_logs')!).length : 0;
      
      // Calcular tamanho aproximado
      const totalSize = Object.keys(localStorage).reduce((size, key) => {
        return size + (localStorage.getItem(key)?.length || 0);
      }, 0);
      
      const sizeInMB = (totalSize / (1024 * 1024)).toFixed(2);
      
      setCacheStats({
        contracts,
        employees,
        templates,
        notifications,
        totalSize: `${sizeInMB} MB`
      });
    } catch (error) {
      console.error('Erro ao calcular estatísticas do cache:', error);
    }
  };

  // Configurações de manutenção movidas para página separada

  // Remover detecção automática de mudanças para evitar re-renderização

  // Remover salvamento automático para evitar re-renderização
  // O salvamento será feito apenas manualmente através do botão "Salvar"

  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  
  // Ref para controlar o foco dos inputs (genérico para input e textarea)
  const focusedInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const isUpdatingRef = useRef(false);

  // Função para manter o foco no input
  const maintainFocus = useCallback(() => {
    if (focusedInputRef.current && !isUpdatingRef.current) {
      // Usar setTimeout para garantir que o foco seja mantido após re-renderização
      setTimeout(() => {
        if (focusedInputRef.current) {
          focusedInputRef.current.focus();
          // Mover cursor para o final do texto (apenas para inputs)
          if (focusedInputRef.current instanceof HTMLInputElement) {
            const length = focusedInputRef.current.value.length;
            focusedInputRef.current.setSelectionRange(length, length);
          }
        }
      }, 0);
    }
  }, []);

  // useEffect para manter o foco após re-renderização
  useEffect(() => {
    maintainFocus();
  });

  // Função para configurar o foco nos inputs (genérico)
  const setupInputFocus = useCallback((inputRef: HTMLInputElement | HTMLTextAreaElement | null) => {
    if (inputRef) {
      focusedInputRef.current = inputRef;
      inputRef.addEventListener('focus', () => {
        focusedInputRef.current = inputRef;
      });
    }
  }, []);

  const handleSettingChange = useCallback((key: keyof AdvancedSettings, value: any) => {
    isUpdatingRef.current = true;
    
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      settingsRef.current = newSettings;
      
      // Detectar mudanças manualmente sem causar re-renderização
      const savedSettings = localStorage.getItem('advanced_settings');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        setHasChanges(JSON.stringify(newSettings) !== JSON.stringify(parsed));
      } else {
        setHasChanges(JSON.stringify(newSettings) !== JSON.stringify(defaultSettings));
      }
      
      return newSettings;
    });
    
    // Resetar flag após um pequeno delay
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 10);
  }, []);

  const applySetting = (key: keyof AdvancedSettings, value: any) => {
    switch (key) {
      case 'debugMode':
        // Ativar/desativar logs de debug no console
        if (value) {
          console.log('🐛 Modo debug ativado');
        } else {
          console.log('🐛 Modo debug desativado');
        }
        break;
        
      case 'logLevel':
        // Atualizar nível de log no localStorage
        localStorage.setItem('log_level', value);
        console.log(`📝 Nível de log alterado para: ${value}`);
        break;
        
      case 'cacheEnabled':
        // Ativar/desativar cache
        if (value) {
          console.log('💾 Cache ativado');
        } else {
          console.log('💾 Cache desativado');
          // Limpar cache quando desativado
          const keysToKeep = ['advanced_settings', 'developer_password', 'user_preferences'];
          Object.keys(localStorage).forEach(key => {
            if (!keysToKeep.includes(key)) {
              localStorage.removeItem(key);
            }
          });
          calculateCacheStats();
        }
        break;
        
      case 'autoSave':
        // Configurar salvamento automático
        localStorage.setItem('auto_save', value.toString());
        console.log(`💾 Salvamento automático: ${value ? 'ativado' : 'desativado'}`);
        break;
        
      case 'notificationsEnabled':
        // Configurar notificações
        localStorage.setItem('notifications_enabled', value.toString());
        console.log(`🔔 Notificações: ${value ? 'ativadas' : 'desativadas'}`);
        break;
        
      case 'performanceMonitoring':
        // Configurar monitoramento de performance
        localStorage.setItem('performance_monitoring', value.toString());
        console.log(`📊 Monitoramento de performance: ${value ? 'ativado' : 'desativado'}`);
        break;
        
      case 'errorReporting':
        // Configurar relatório de erros
        localStorage.setItem('error_reporting', value.toString());
        console.log(`📧 Relatório de erros: ${value ? 'ativado' : 'desativado'}`);
        break;
        
      case 'analyticsEnabled':
        // Configurar analytics
        localStorage.setItem('analytics_enabled', value.toString());
        console.log(`📈 Analytics: ${value ? 'ativado' : 'desativado'}`);
        break;
        
      // Configurações de manutenção movidas para página separada
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      // Remover detecção de mudanças para evitar re-renderização
      
      // Salvar no localStorage
      localStorage.setItem('advanced_settings', JSON.stringify(settings));
      
      // Configurações de manutenção movidas para página separada
      
      // Aplicar todas as configurações
      Object.entries(settings).forEach(([key, value]) => {
        applySetting(key as keyof AdvancedSettings, value);
      });
      
      setHasChanges(false);
      
      // Simular delay de salvamento
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Recalcular estatísticas do cache
      calculateCacheStats();
      
      // Mostrar feedback
      alert('Configurações salvas e aplicadas com sucesso!');
      
    } catch (error) {
      console.error('❌ Erro ao salvar configurações:', error);
      alert('Erro ao salvar configurações. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Tem certeza que deseja redefinir todas as configurações para os valores padrão?')) {
      setSettings(defaultSettings);
      localStorage.removeItem('advanced_settings');
    }
  };

  const handleClearCache = () => {
    if (window.confirm('Tem certeza que deseja limpar todo o cache? Isso pode afetar a performance temporariamente.')) {
      // Limpar cache real (manter apenas configurações essenciais)
      const keysToKeep = ['advanced_settings', 'developer_password', 'user_preferences'];
      const keysToRemove = Object.keys(localStorage).filter(key => !keysToKeep.includes(key));
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });
      
      // Recalcular estatísticas
      calculateCacheStats();
      
      alert('Cache limpo com sucesso!');
    }
  };

  const handleExportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'advanced-settings.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImportSettings = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target?.result as string);
          setSettings({ ...defaultSettings, ...imported });
        } catch (error) {
          alert('Erro ao importar configurações. Verifique se o arquivo é válido.');
        }
      };
      reader.readAsText(file);
    }
  };

  const SettingCard = ({ 
    title, 
    description, 
    children, 
    icon: Icon 
  }: { 
    title: string; 
    description: string; 
    children: React.ReactNode; 
    icon: React.ComponentType<any>;
  }) => (
    <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-600">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Settings className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-slate-900">PlayKids</h1>
                <p className="text-xs text-slate-500">Configurações Avançadas</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {hasChanges && (
                <div className="flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-700 rounded-lg text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  Alterações não salvas
                </div>
              )}
              <button
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Salvar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Debug & Logging */}
          <SettingCard
            title="Debug & Logging"
            description="Configurações de depuração e registro de logs"
            icon={Zap}
          >
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium text-slate-700">Modo Debug</label>
                  <p className="text-sm text-slate-500">Ativa logs detalhados para desenvolvimento</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.debugMode}
                    onChange={(e) => handleSettingChange('debugMode', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nível de Log
                </label>
                <select
                  value={settings.logLevel}
                  onChange={(e) => handleSettingChange('logLevel', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="debug">Debug (Todos os logs)</option>
                  <option value="info">Info (Informações importantes)</option>
                  <option value="warn">Warning (Avisos e erros)</option>
                  <option value="error">Error (Apenas erros)</option>
                </select>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium text-slate-700">Relatório de Erros</label>
                  <p className="text-sm text-slate-500">Enviar erros automaticamente para análise</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.errorReporting}
                    onChange={(e) => handleSettingChange('errorReporting', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </SettingCard>

          {/* Performance */}
          <SettingCard
            title="Performance"
            description="Configurações de cache e otimização"
            icon={Zap}
          >
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium text-slate-700">Cache Ativado</label>
                  <p className="text-sm text-slate-500">Melhora a performance do sistema</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.cacheEnabled}
                    onChange={(e) => handleSettingChange('cacheEnabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Tamanho do Cache (MB)
                </label>
                <input
                  type="range"
                  min="10"
                  max="200"
                  value={settings.cacheSize}
                  onChange={(e) => handleSettingChange('cacheSize', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>10 MB</span>
                  <span className="font-medium">{settings.cacheSize} MB</span>
                  <span>200 MB</span>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Estatísticas do Cache</span>
                  <button
                    onClick={() => setShowCacheDetails(!showCacheDetails)}
                    className="text-blue-600 hover:text-blue-700 text-sm"
                  >
                    {showCacheDetails ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {showCacheDetails && (
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>Contratos: {cacheStats.contracts}</div>
                    <div>Funcionários: {cacheStats.employees}</div>
                    <div>Templates: {cacheStats.templates}</div>
                    <div>Notificações: {cacheStats.notifications}</div>
                    <div className="col-span-2 font-medium">Total: {cacheStats.totalSize}</div>
                  </div>
                )}
                <button
                  onClick={handleClearCache}
                  className="mt-2 flex items-center gap-1 text-red-600 hover:text-red-700 text-sm"
                >
                  <Trash2 className="w-3 h-3" />
                  Limpar Cache
                </button>
              </div>
            </div>
          </SettingCard>

          {/* Notifications */}
          <SettingCard
            title="Notificações"
            description="Configurações de alertas e notificações"
            icon={Shield}
          >
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium text-slate-700">Notificações Ativadas</label>
                  <p className="text-sm text-slate-500">Receber alertas do sistema</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.notificationsEnabled}
                    onChange={(e) => handleSettingChange('notificationsEnabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium text-slate-700">Monitoramento de Performance</label>
                  <p className="text-sm text-slate-500">Acompanhar métricas em tempo real</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.performanceMonitoring}
                    onChange={(e) => handleSettingChange('performanceMonitoring', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium text-slate-700">Salvamento Automático</label>
                  <p className="text-sm text-slate-500">Salvar alterações automaticamente</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.autoSave}
                    onChange={(e) => handleSettingChange('autoSave', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </SettingCard>

          {/* System */}
          <SettingCard
            title="Sistema"
            description="Configurações gerais do sistema"
            icon={Database}
          >
            <div className="space-y-4">
              <div className="text-center py-4">
                <p className="text-slate-500 text-sm">
                  Configurações de manutenção movidas para página dedicada
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium text-slate-700">Analytics</label>
                  <p className="text-sm text-slate-500">Coletar dados de uso (anônimos)</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.analyticsEnabled}
                    onChange={(e) => handleSettingChange('analyticsEnabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </SettingCard>

          {/* Manutenção */}
          <SettingCard
            title="Configurações de Manutenção"
            description="Personalizar página de manutenção"
            icon={Wrench}
          >
            <div className="text-center py-8">
              <Wrench className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                Configurações de Manutenção
              </h3>
              <p className="text-slate-600 mb-6">
                Personalize a tela de manutenção do sistema com opções avançadas de customização.
              </p>
              <button
                onClick={() => window.location.href = '/admin/configuracoes-manutencao'}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Wrench className="w-4 h-4" />
                Abrir Configurações de Manutenção
              </button>
            </div>
          </SettingCard>
        </div>

        {/* Actions */}
        <div className="mt-8 bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Ações</h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Redefinir Padrões
            </button>
            <button
              onClick={handleExportSettings}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Exportar Configurações
            </button>
            <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer">
              <Upload className="w-4 h-4" />
              Importar Configurações
              <input
                type="file"
                accept=".json"
                onChange={handleImportSettings}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};
